// TaskDrawer — generic slide-over for a single task's detail. Reusable across
// the Home Tasks card and the Tasks page. Dispatches its body by task type
// (component pattern) and lazily fetches the detail only while open.
//
// design ref: styles.css:1046 (.drawer / .drawer__header / .drawer__tabs /
// .drawer__body / .drawer__footer) + drawers.jsx TaskDrawer.

import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TbArrowRight, TbCheck, TbFingerprint, TbX } from "react-icons/tb";

import Avatar from "components/ds/Avatar";
import PloyButton from "components/ds/PloyButton";
import { MF_TASKS, type MFTaskItem } from "state/members/tasks";

import OffboardingView from "./OffboardingView";
import TaskDetailBody from "./TaskDetailBody";
import { useOffboardingChecklist } from "./useOffboardingChecklist";
import { useTaskApproval } from "./useTaskApproval";
import {
  actionLabels,
  approvalResourceName,
  APPROVABLE,
  canAnswerTask,
  completedResolutionMeta,
  INDIRECT_REMEDIATION_ENTITY_TYPE,
  readIndirectRemediation,
  respondToast,
  taskStatusMeta,
  timeAgo,
  typeMeta,
} from "./taskMeta";
import Chip from "components/ds/Chip";

// Elongated status/type dot (12×8, fully rounded) — design's inline dot.
const Dot = ({ color }: { color: string }) => (
  <Box w="12px" h="8px" borderRadius="full" bg={color} flexShrink={0} />
);

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    px="14px"
    py="10px"
    fontSize="md"
    fontWeight={500}
    color={active ? "p.700" : "neutral.700"}
    borderBottomWidth="2px"
    borderColor={active ? "p.500" : "transparent"}
    mb="-1px"
    transition="color 120ms, border-color 120ms"
  >
    {children}
  </Box>
);

// Read-only outcome banner shown at the top of a completed task's detail.
const ResolutionBanner = ({ task }: { task: MFTaskItem }) => {
  const res = completedResolutionMeta(task.result, task.entityType);
  const tint =
    res.tone === "success"
      ? { bg: "success.50", border: "success.200" }
      : res.tone === "error"
        ? { bg: "error.50", border: "error.200" }
        : { bg: "neutral.50", border: "neutral.200" };
  return (
    <Flex
      align="center"
      gap="sm"
      px="14px"
      py="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={tint.border}
      bg={tint.bg}
      borderRadius="component.2xl"
    >
      <Chip tone={res.tone} icon={<Icon as={res.positive ? TbCheck : TbX} boxSize="12px" />}>
        {res.label}
      </Chip>
      {task.completedAt && (
        <Text fontSize="smd" color="neutral.700">
          Closed {timeAgo(task.completedAt)}
        </Text>
      )}
    </Flex>
  );
};

export interface TaskDrawerProps {
  /** The selected task, or null when the drawer is closed. */
  task: MFTaskItem | null;
  onClose: () => void;
}

const TaskDrawer = ({ task, onClose }: TaskDrawerProps) => {
  const navigate = useNavigate();
  const toast = useToast();

  // Retain the last task during the close animation so content doesn't blank
  // out (Chakra keeps DrawerContent mounted while it animates away).
  const [shown, setShown] = useState<MFTaskItem | null>(task);
  const [tab, setTab] = useState<"details" | "history">("details");
  const [note, setNote] = useState("");

  const isOpen = Boolean(task);
  const taskKey =
    task?.assigneeId ?? task?.offboardingId ?? task?.campaignCycleId ?? null;
  const shownKey =
    shown?.assigneeId ?? shown?.offboardingId ?? shown?.campaignCycleId ?? null;

  // Sync the retained `shown` to the live task DURING render (the supported
  // "adjust state on prop change" pattern), so the header/body and the respond
  // binding never lag a render behind the detail query on (re)open — otherwise
  // the first committed frame would show the PREVIOUS task. On close (task=null)
  // we keep the last `shown` for the exit animation.
  if (task && taskKey !== shownKey) setShown(task);

  useEffect(() => {
    if (task) {
      setTab("details");
      setNote("");
    }
  }, [taskKey, task]);

  // Keyed off the live `task` (not the retained `shown`) so the queries disable
  // once the drawer closes instead of holding an enabled query indefinitely.
  const assigneeId =
    task?.taskType === "assignment" ? task.assigneeId : undefined;
  const offboardingId =
    task?.taskType === "offboarding" ? task.offboardingId : undefined;

  const detailQuery = MF_TASKS.useGetTaskDetail(assigneeId);
  const offboardingQuery = MF_TASKS.useGetOffboardingDetail(offboardingId);
  const { respond, isLoading: responding } =
    MF_TASKS.useRespondToTask(shown?.assigneeId);
  // The detail is the fresher word on whether this stage needs a passkey.
  const approval = useTaskApproval(
    shown && {
      ...shown,
      passkeyRequired: detailQuery.data?.passkeyRequired ?? shown.passkeyRequired,
    },
    respond,
  );
  const busy = responding || approval.isStarting;
  const offboarding = useOffboardingChecklist(offboardingQuery.data);

  const isOffboarding = shown?.taskType === "offboarding";
  // Completed tasks carry a close time; render them read-only (no actions).
  const isCompleted = Boolean(shown?.completedAt);

  const actionable =
    !!shown &&
    !isCompleted &&
    shown.taskType === "assignment" &&
    !!shown.assigneeId &&
    APPROVABLE.has(shown.entityType) &&
    canAnswerTask(shown.entityType, detailQuery.data?.metadata);

  const decide = (decision: "approve" | "deny") => {
    if (!shown) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entity = detailQuery.data?.entity as any;
    // An indirect-remediation task names its resource in the metadata bag
    // rather than on `entity`, so the toast's resource clause needs this too.
    const remediation =
      shown.entityType === INDIRECT_REMEDIATION_ENTITY_TYPE
        ? readIndirectRemediation(detailQuery.data?.metadata)
        : null;
    const resourceName =
      approvalResourceName(entity?.selectableResource) ??
      entity?.resourceName ??
      entity?.selectableResourceName ??
      remediation?.resourceName ??
      undefined;
    const { title, description } = respondToast(
      shown.entityType,
      decision,
      shown.memberName,
      resourceName,
    );
    const comment = note.trim() || undefined;
    // An approval that went to the signing page is confirmed there.
    const answered =
      decision === "approve"
        ? approval.approve(comment).then((outcome) => outcome === "approved")
        : respond(decision, comment).then(() => true);
    answered
      .then((done) => {
        if (!done) return;
        toast({ status: decision === "approve" ? "success" : "info", title, description });
        onClose();
      })
      // The api layer surfaces the error toast; keep the drawer open (no
      // success toast / close) and swallow so it isn't an unhandled rejection.
      .catch(() => undefined);
  };

  const goToPage = () => {
    if (shown?.taskType === "access_review_campaign")
      navigate("/members/access-reviews");
    onClose();
  };

  if (!shown) return null;

  const type = typeMeta(shown.entityType);
  const status = taskStatusMeta(shown.status, shown.entityType);
  const labels = actionLabels(shown.entityType);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" autoFocus={false}>
      <DrawerOverlay
        bg="linear-gradient(90deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.14) 100%)"
        sx={{
          backdropFilter: "blur(8px) saturate(120%)",
          WebkitBackdropFilter: "blur(8px) saturate(120%)",
        }}
      />
      <DrawerContent
        w="38vw"
        minW="440px"
        maxW="640px"
        bg="neutral.25"
        boxShadow="-16px 0 32px rgba(16,24,40,0.08), -4px 0 8px rgba(16,24,40,0.04)"
        display="flex"
        flexDirection="column"
      >
        {/* .drawer__header */}
        <Flex
          align="flex-start"
          gap="md"
          px="20px"
          pt="20px"
          pb="md"
          borderBottomWidth="1px"
          borderColor="neutral.200"
        >
          <Avatar
            name={shown.memberName ?? shown.title}
            src={shown.memberImageUrl ?? undefined}
            size="md"
            ring
          />
          <Box flex="1" minW={0}>
            <Text
              fontSize="lg"
              fontWeight={600}
              color="neutral.950"
              lineHeight="1.3"
              sx={{ textWrap: "balance" }}
            >
              {shown.title}
            </Text>
            {/* .drawer__header-meta — design inlines 14px on these spans */}
            <HStack spacing="sm" mt="1xs" flexWrap="wrap" fontSize="md" color="neutral.700">
              <Box as="span">Type:</Box>
              <HStack as="span" spacing="1xs" color="neutral.900" fontWeight={500}>
                <Dot color={type.color} />
                <Box as="span">{type.label}</Box>
              </HStack>
              <Box as="span">·</Box>
              <Box as="span">Status:</Box>
              <HStack as="span" spacing="1xs" color="neutral.900" fontWeight={500}>
                <Dot color={status.color} />
                <Box as="span">{status.label}</Box>
              </HStack>
            </HStack>
          </Box>
          <Box
            as="button"
            type="button"
            onClick={onClose}
            aria-label="Close"
            w="32px"
            h="32px"
            borderRadius="component.lg"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            color="neutral.700"
            flexShrink={0}
            transition="background-color 120ms"
            _hover={{ bg: "neutral.100" }}
            _active={{ transform: "scale(0.96)" }}
          >
            <Icon as={TbX} boxSize="18px" />
          </Box>
        </Flex>

        {/* .drawer__tabs */}
        <HStack spacing={0} px="20px" borderBottomWidth="1px" borderColor="neutral.200">
          <TabButton active={tab === "details"} onClick={() => setTab("details")}>
            Details
          </TabButton>
          <TabButton active={tab === "history"} onClick={() => setTab("history")}>
            History
          </TabButton>
        </HStack>

        {/* .drawer__body */}
        <DrawerBody px="20px" py="20px" display="flex" flexDirection="column" gap="20px">
          {tab === "details" ? (
            <>
              {isCompleted && <ResolutionBanner task={shown} />}
              {isOffboarding ? (
                offboardingQuery.isLoading ? (
                  <Flex align="center" justify="center" py="2xl">
                    <Spinner size="sm" color="neutral.500" />
                  </Flex>
                ) : offboardingQuery.data ? (
                  <OffboardingView detail={offboardingQuery.data} controller={offboarding} />
                ) : (
                  <Text fontSize="smd" color="neutral.600">
                    Couldn't load this offboarding task.
                  </Text>
                )
              ) : (
                <TaskDetailBody
                  task={shown}
                  detail={detailQuery.data}
                  detailLoading={detailQuery.isLoading}
                  showNote={actionable}
                  note={note}
                  onNoteChange={setNote}
                />
              )}
            </>
          ) : (
            <Flex direction="column" align="center" justify="center" py="3xl" gap="2xs">
              <Text fontSize="md" fontWeight={600} color="neutral.800">
                No history yet
              </Text>
              <Text fontSize="smd" color="neutral.600" textAlign="center">
                Activity for this task will appear here.
              </Text>
            </Flex>
          )}
        </DrawerBody>

        {/* .drawer__footer */}
        <Flex
          align="center"
          gap="sm"
          px="20px"
          py="lg"
          borderTopWidth="1px"
          borderColor="neutral.200"
          bg="neutral.25"
        >
          {actionable ? (
            <>
              <PloyButton
                variant="success"
                leftIcon={
                  <Icon as={approval.needsPasskey ? TbFingerprint : TbCheck} boxSize="16px" />
                }
                isLoading={busy}
                onClick={() => decide("approve")}
              >
                {approval.needsPasskey ? `${labels.approve} with passkey` : labels.approve}
              </PloyButton>
              <PloyButton
                variant="danger"
                leftIcon={<Icon as={TbX} boxSize="16px" />}
                isDisabled={busy}
                onClick={() => decide("deny")}
              >
                {labels.deny}
              </PloyButton>
            </>
          ) : isOffboarding && offboarding.hasAccounts && !isCompleted ? (
            // Per-account actions live on each row (see OffboardingView) — the
            // footer just summarises progress and offers a close.
            <>
              <Text fontSize="smd" color="neutral.700">
                {offboarding.remaining === 0
                  ? "All accounts handled."
                  : `${offboarding.remaining} account${offboarding.remaining === 1 ? "" : "s"} remaining`}
              </Text>
              <PloyButton variant="secondary" ml="auto" onClick={onClose}>
                Done
              </PloyButton>
            </>
          ) : shown.taskType === "access_review_campaign" ? (
            <PloyButton
              variant="secondary"
              rightIcon={<Icon as={TbArrowRight} boxSize="16px" />}
              onClick={goToPage}
            >
              Open
            </PloyButton>
          ) : isCompleted ? (
            <Text fontSize="smd" color="neutral.600">
              This task is closed.
            </Text>
          ) : null}
        </Flex>
      </DrawerContent>
    </Drawer>
  );
};

export default TaskDrawer;
